// Pure, DOM-free image processing shared by the main thread and the image
// Web Worker (image-worker.ts). Everything here uses only APIs available in
// both contexts (Blob, DataView, createImageBitmap, OffscreenCanvas), so the
// worker can import it untouched. Keep it that way — no `window`, no React, no
// `document` except the guarded canvas fallback in downscaleToBlob.

// Walk the TIFF/DNG IFD tree and return the largest embedded JPEG (the
// full-resolution preview). ProRAW stores it either as a JPEG-compressed strip
// (Compression=7) or via the JPEGInterchangeFormat tag pair. We pick the
// largest segment that begins with an SOI marker so we get the full preview,
// not the tiny EXIF thumbnail. Returns null when nothing usable is found.
export async function extractEmbeddedJpeg(source: Blob): Promise<Blob | null> {
  const buf = await source.arrayBuffer();
  if (buf.byteLength < 8) return null;
  const dv = new DataView(buf);

  const b0 = dv.getUint8(0), b1 = dv.getUint8(1);
  let le: boolean;
  if (b0 === 0x49 && b1 === 0x49) le = true;        // 'II' little-endian
  else if (b0 === 0x4d && b1 === 0x4d) le = false;  // 'MM' big-endian
  else return scanForLargestJpeg(dv); // not a TIFF — try a raw byte scan
  if (dv.getUint16(2, le) !== 42) return scanForLargestJpeg(dv);

  const candidates: Array<{ offset: number; length: number }> = [];
  const visited = new Set<number>();

  const readValues = (type: number, count: number, valueFieldOffset: number): number[] => {
    const typeSize = type === 3 ? 2 : 4; // SHORT=3 (2 bytes), LONG/IFD=4 bytes
    const total = typeSize * count;
    const base = total <= 4 ? valueFieldOffset : dv.getUint32(valueFieldOffset, le);
    const out: number[] = [];
    for (let j = 0; j < count; j++) {
      const o = base + j * typeSize;
      if (o + typeSize > buf.byteLength) break;
      out.push(typeSize === 2 ? dv.getUint16(o, le) : dv.getUint32(o, le));
    }
    return out;
  };

  const readIFD = (ifdOffset: number, depth: number) => {
    if (depth > 8 || ifdOffset <= 0 || ifdOffset + 2 > buf.byteLength || visited.has(ifdOffset)) return;
    visited.add(ifdOffset);
    const entries = dv.getUint16(ifdOffset, le);
    let compression = 0, jpegOffset = 0, jpegLength = 0;
    let stripOffsets: number[] = [], stripByteCounts: number[] = [];
    const subIFDs: number[] = [];

    for (let i = 0; i < entries; i++) {
      const e = ifdOffset + 2 + i * 12;
      if (e + 12 > buf.byteLength) break;
      const tag = dv.getUint16(e, le);
      const type = dv.getUint16(e + 2, le);
      const count = dv.getUint32(e + 4, le);
      const valueFieldOffset = e + 8;
      switch (tag) {
        case 0x0103: compression = readValues(type, count, valueFieldOffset)[0] ?? 0; break; // Compression
        case 0x0111: stripOffsets = readValues(type, count, valueFieldOffset); break;        // StripOffsets
        case 0x0117: stripByteCounts = readValues(type, count, valueFieldOffset); break;     // StripByteCounts
        case 0x0201: jpegOffset = readValues(type, count, valueFieldOffset)[0] ?? 0; break;  // JPEGInterchangeFormat
        case 0x0202: jpegLength = readValues(type, count, valueFieldOffset)[0] ?? 0; break;  // JPEGInterchangeFormatLength
        case 0x014a: subIFDs.push(...readValues(type, count, valueFieldOffset)); break;      // SubIFDs
      }
    }

    if (jpegOffset > 0 && jpegLength > 0) candidates.push({ offset: jpegOffset, length: jpegLength });
    // Single-strip JPEG-compressed image (the common ProRAW preview layout).
    // Multi-strip JPEG would be non-contiguous, so we skip it and let the
    // JPEGInterchangeFormat tag or the byte-scan fallback handle those.
    if (compression === 7 && stripOffsets.length === 1 && stripByteCounts.length === 1) {
      candidates.push({ offset: stripOffsets[0], length: stripByteCounts[0] });
    }

    for (const s of subIFDs) readIFD(s, depth + 1);
    const nextPtr = ifdOffset + 2 + entries * 12;
    if (nextPtr + 4 <= buf.byteLength) readIFD(dv.getUint32(nextPtr, le), depth + 1);
  };

  readIFD(dv.getUint32(4, le), 0);

  let best: { offset: number; length: number } | null = null;
  for (const c of candidates) {
    if (c.offset + 2 > buf.byteLength) continue;
    if (dv.getUint8(c.offset) !== 0xff || dv.getUint8(c.offset + 1) !== 0xd8) continue; // SOI
    const length = Math.min(c.offset + c.length, buf.byteLength) - c.offset;
    if (!best || length > best.length) best = { offset: c.offset, length };
  }
  if (best) return new Blob([buf.slice(best.offset, best.offset + best.length)], { type: 'image/jpeg' });
  return scanForLargestJpeg(dv);
}

// Last-resort fallback: find the largest FFD8…FFD9 run in the file. Used when
// the IFD walk finds nothing (truncated tags, odd vendor layout).
function scanForLargestJpeg(dv: DataView): Blob | null {
  const n = dv.byteLength;
  let best: { start: number; length: number } | null = null;
  let i = 0;
  while (i < n - 1) {
    if (dv.getUint8(i) === 0xff && dv.getUint8(i + 1) === 0xd8) {
      for (let j = i + 2; j < n - 1; j++) {
        if (dv.getUint8(j) === 0xff && dv.getUint8(j + 1) === 0xd9) {
          const length = j + 2 - i;
          if (!best || length > best.length) best = { start: i, length };
          i = j + 2;
          break;
        }
      }
      if (i < n - 1 && !(dv.getUint8(i) === 0xff && dv.getUint8(i + 1) === 0xd8)) continue;
    }
    i++;
  }
  if (!best || best.length < 1024) return null;
  return new Blob([dv.buffer.slice(best.start, best.start + best.length)], { type: 'image/jpeg' });
}

// Read a JPEG's pixel dimensions from its SOF marker without decoding pixels —
// a few hundred bytes of header scan. Lets us compute the resize target up
// front so createImageBitmap downsamples during decode. Returns null for
// non-JPEG or malformed data (caller falls back to a full-decode probe).
function readJpegSize(buf: ArrayBuffer): { width: number; height: number } | null {
  const dv = new DataView(buf);
  if (dv.byteLength < 4 || dv.getUint8(0) !== 0xff || dv.getUint8(1) !== 0xd8) return null;
  let pos = 2;
  while (pos + 9 < dv.byteLength) {
    if (dv.getUint8(pos) !== 0xff) { pos++; continue; }
    let marker = dv.getUint8(pos + 1);
    while (marker === 0xff && pos + 1 < dv.byteLength) { pos++; marker = dv.getUint8(pos + 1); } // skip fill bytes
    // SOF0..SOF15 hold the frame dimensions, except DHT(C4)/JPG(C8)/DAC(CC).
    if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      const height = dv.getUint16(pos + 5, false);
      const width = dv.getUint16(pos + 7, false);
      return { width, height };
    }
    if (marker === 0xd8 || marker === 0xd9 || (marker >= 0xd0 && marker <= 0xd7)) { pos += 2; continue; } // marker w/o length
    const segLen = dv.getUint16(pos + 2, false);
    if (segLen < 2) return null;
    pos += 2 + segLen;
  }
  return null;
}

// Read the EXIF orientation tag (1–8) from a JPEG's APP1 segment without
// decoding pixels. Orientations 5–8 are 90°/270° rotations, meaning the
// displayed image is portrait even though the stored pixels are landscape.
// Returns 1 (no rotation) for non-JPEG or when the tag is absent.
function readExifOrientation(buf: ArrayBuffer): number {
  const dv = new DataView(buf);
  if (dv.byteLength < 4 || dv.getUint16(0, false) !== 0xffd8) return 1; // not JPEG
  let pos = 2;
  while (pos + 4 < dv.byteLength) {
    if (dv.getUint8(pos) !== 0xff) { pos++; continue; }
    const marker = dv.getUint8(pos + 1);
    if (marker === 0xe1) { // APP1 (Exif)
      const seg = pos + 4;
      if (seg + 8 <= dv.byteLength
        && dv.getUint8(seg) === 0x45 && dv.getUint8(seg + 1) === 0x78      // 'Ex'
        && dv.getUint8(seg + 2) === 0x69 && dv.getUint8(seg + 3) === 0x66) { // 'if'
        const tiff = seg + 6; // skip "Exif\0\0"
        if (tiff + 8 > dv.byteLength) return 1;
        const le = dv.getUint8(tiff) === 0x49; // 'II' little-endian, else 'MM'
        const ifd0 = tiff + dv.getUint32(tiff + 4, le);
        if (ifd0 + 2 > dv.byteLength) return 1;
        const entries = dv.getUint16(ifd0, le);
        for (let i = 0; i < entries; i++) {
          const e = ifd0 + 2 + i * 12;
          if (e + 12 > dv.byteLength) break;
          if (dv.getUint16(e, le) === 0x0112) { // Orientation
            const v = dv.getUint16(e + 8, le);
            return v >= 1 && v <= 8 ? v : 1;
          }
        }
      }
      return 1;
    }
    if (marker === 0xd8 || marker === 0xd9 || (marker >= 0xd0 && marker <= 0xd7)) { pos += 2; continue; }
    if (marker >= 0xc0 && marker <= 0xcf) break; // frame markers follow all APPn — stop
    const segLen = dv.getUint16(pos + 2, false);
    if (segLen < 2) break;
    pos += 2 + segLen;
  }
  return 1;
}

async function blobFromCanvas(
  canvas: OffscreenCanvas | HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: mimeType, quality });
  }
  return new Promise<Blob>((resolve, reject) => {
    (canvas as HTMLCanvasElement).toBlob(
      b => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      mimeType,
      quality,
    );
  });
}

/**
 * Decode + downscale a (canvas-decodable) image with minimal peak memory.
 *
 * Uses createImageBitmap's resize options so the JPEG decoder downsamples
 * during decode rather than materializing the full-resolution bitmap first,
 * then encodes via OffscreenCanvas. Falls back to a regular <canvas> only when
 * OffscreenCanvas is unavailable AND a DOM exists (i.e. main thread on an old
 * browser) — inside a worker OffscreenCanvas is always present.
 */
export async function downscaleToBlob(
  source: Blob,
  opts: { maxDimension: number; quality: number; mimeType: string },
): Promise<Blob> {
  const { maxDimension, quality, mimeType } = opts;
  const buf = await source.arrayBuffer();

  // Resolve the DISPLAY dimensions (after EXIF rotation). createImageBitmap
  // with imageOrientation:'from-image' rotates the image BEFORE applying
  // resizeWidth/resizeHeight, so the target must be in display space — for a
  // portrait photo (EXIF 5–8) we swap the raw landscape dims, otherwise the
  // rotated image gets squashed into a landscape box (the stretch bug).
  let dispW: number, dispH: number;
  const headerSize = readJpegSize(buf);
  if (headerSize) {
    const orientation = readExifOrientation(buf);
    const swap = orientation >= 5 && orientation <= 8;
    dispW = swap ? headerSize.height : headerSize.width;
    dispH = swap ? headerSize.width : headerSize.height;
  } else {
    // Non-JPEG (png/webp) or unreadable header: probe with orientation applied
    // so we get display dims directly. Rare on the heavy path.
    const probe = await createImageBitmap(source, { imageOrientation: 'from-image' });
    dispW = probe.width; dispH = probe.height;
    probe.close();
  }

  const longEdge = Math.max(dispW, dispH) || maxDimension;
  const scale = Math.min(1, maxDimension / longEdge);
  const targetW = Math.max(1, Math.round(dispW * scale));
  const targetH = Math.max(1, Math.round(dispH * scale));

  const bitmap = await createImageBitmap(source, {
    resizeWidth: targetW,
    resizeHeight: targetH,
    resizeQuality: 'high',
    imageOrientation: 'from-image', // bake EXIF rotation; canvas re-encode drops the tag
  });

  let canvas: OffscreenCanvas | HTMLCanvasElement;
  if (typeof OffscreenCanvas !== 'undefined') {
    canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  } else if (typeof document !== 'undefined') {
    canvas = Object.assign(document.createElement('canvas'), { width: bitmap.width, height: bitmap.height });
  } else {
    bitmap.close();
    throw new Error('No canvas implementation available for image downscale');
  }
  const ctx = canvas.getContext('2d') as
    | OffscreenCanvasRenderingContext2D
    | CanvasRenderingContext2D
    | null;
  if (!ctx) {
    bitmap.close();
    throw new Error('No 2D context available for image downscale');
  }
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  try {
    return await blobFromCanvas(canvas, mimeType, quality);
  } catch {
    // Some Safari versions can't encode WebP via canvas — fall back to JPEG.
    return blobFromCanvas(canvas, 'image/jpeg', quality);
  }
}

export interface HeavyImageOptions {
  isRaw: boolean;
  maxDimension: number;
  quality: number;
  mimeType: string;
}

/**
 * The full heavy-image pipeline: for RAW/DNG, pull out the embedded JPEG
 * preview first (browsers can't demosaic raw sensor data); then downscale with
 * the low-memory createImageBitmap path. Throws a user-facing Spanish message
 * when a RAW file has no usable embedded preview.
 *
 * This is the unit the Web Worker runs. It's also called directly on the main
 * thread as a fallback when the worker is unavailable.
 */
export async function processHeavyImage(source: Blob, opts: HeavyImageOptions): Promise<Blob> {
  let input: Blob = source;
  if (opts.isRaw) {
    const preview = await extractEmbeddedJpeg(source);
    if (!preview) {
      throw new Error('No se pudo procesar esta imagen RAW. Exporta la foto a JPEG e inténtalo de nuevo.');
    }
    input = preview;
  }
  return downscaleToBlob(input, {
    maxDimension: opts.maxDimension,
    quality: opts.quality,
    mimeType: opts.mimeType,
  });
}
